/**
 * @typedef ApiConfiguration
 * @property {String} serviceName The service name.
 * @property {Boolean} [reload=false] Fetch on app initialization.
 * @property {Function} service Service method.
 * @property {String} fetchAction Action to trigger a request.
 * @property {String} successAction Action to dispatch upon success.
 * @property {String} [failureAction] Action to dispatch upon failure.
 * @property {Boolean} [throwOnFail] Throw on request failure.
 */
import { STATUS_CODE_CANCEL } from "constant/app";
import get from "lodash/get";
import { all, call, put, takeLatest } from "redux-saga/effects";
import { USER_LOGIN, USER_LOGOUT } from "redux-store/auth/actions";
import {
  addErrorMessageAction,
  clearMessagesBySourceAction,
} from "redux-store/messages";
import { auth } from "store/auth";
import { getErrorMessage } from "util/response";

import { createApiAction } from "./actions";

/**
 * Generate the appropriate failure actions on api failure.
 *
 * @param {ApiConfiguration} config ApiConfig object
 * @param {AxiosResponse} response Axios Response
 *
 * @return {Array} Array of Redux put actions.
 * @private
 */
function _getFailureActions(config, response) {
  const { failureAction, serviceName, successAction } = config;
  const { status } = response;

  const errorMessage = getErrorMessage(response);
  const failureActions = [];

  if (status !== STATUS_CODE_CANCEL && errorMessage) {
    // Don't push 'request cancelled' or empty error messages to user.
    const displayErrorMessageAction = addErrorMessageAction(
      errorMessage,
      serviceName,
      status,
    );
    failureActions.push(put(displayErrorMessageAction));
  }

  if (failureAction) {
    const apiFailureAction = createApiAction(
      failureAction,
      response,
      errorMessage,
    );

    failureActions.push(put(apiFailureAction));
  }

  if (status === 401) {
    // We don't know anything about expired sessions.
    // All we know is that the API said you need to log in.
    // We make a best guess about the expired session via the existence
    // of an api token.
    // TODO(jhohman): API needs to tell us if the token is expired. No guessing.
    const sessionExpired = Boolean(auth.getToken());

    const logoutUserAction = {
      type: USER_LOGOUT,
      payload: {
        failureAction,
        serviceName,
        successAction,
        sessionExpired,
        statusCode: status,
      },
    };

    failureActions.push(put(logoutUserAction));
  }

  return failureActions;
}

/**
 * Generate the appropriate success actions on api success.
 *
 * @param {ApiConfiguration} config ApiConfig object
 * @param {AxiosResponse} response Axios Response
 * @param {Object} action Action
 *
 * @return {Array} Array of Redux put actions.
 * @private
 */
function _getSuccessActions(config, response, action) {
  const { serviceName, successAction } = config;

  const clearMessagesAction = clearMessagesBySourceAction(serviceName);
  const successActions = [put(clearMessagesAction)];

  if (successAction) {
    const { serviceArgs = [], successMessage = "" } = get(
      action,
      "payload",
      {},
    );
    const apiSuccessAction = createApiAction(
      successAction,
      response,
      successMessage,
      { serviceArgs },
    );

    successActions.push(put(apiSuccessAction));
  }

  return successActions;
}

/**
 * Central handler for all api requests.
 *
 * @param {ApiConfiguration} config ApiConfig object
 * @param {Action} action Redux Action
 *
 * @return {AxiosResponse} Response
 */
function* genericApiCaller(config, action) {
  const { serviceName, service, throwOnFail = false } = config;
  const { reject, resolve, serviceArgs = [] } = get(action, "payload", {});

  let response;
  try {
    if (Array.isArray(serviceArgs)) {
      response = yield call(service, ...serviceArgs);
    } else {
      response = yield call(service, serviceArgs);
    }
  } catch (response) {
    console.error(`${serviceName} responded`, response.status);

    if (reject) {
      reject(response);
    }

    const failureActions = _getFailureActions(config, response);

    yield all(failureActions);

    if (throwOnFail) {
      throw response;
    }

    return response;
  }

  if (resolve) {
    resolve(response);
  }

  const successActions = _getSuccessActions(config, response, action);

  yield all(successActions);
  return response;
}

/**
 * @param {ApiConfiguration} config ApiConfig object
 *
 * @return {GeneratorFunction} Api Saga
 */
function createApiSaga(config) {
  const { fetchAction, reload = false } = config;

  const apiCaller = genericApiCaller.bind(null, config);

  return reload
    ? reloadApiSaga.bind(null, apiCaller)
    : apiServiceSaga.bind(null, fetchAction, apiCaller);
}

/**
 * Convenience wrapper around genericApiCaller for making direct saga
 * calls to a service.
 *
 * @param {Object} config The call configuration.
 * @param {Function} config.service The service to call.
 * @param {String} config.serviceName The service's name.
 * @param {Array} [config.serviceArgs] Arguments to the service call.
 * @param {Boolean} [config.throwOnFail=true] Throw on request failure.
 *
 * @throws {AxiosResponse} Response object.
 * @return {AxiosResponse} Response object.
 */
function* directServiceSaga(config) {
  const {
    failureAction,
    service,
    serviceName,
    serviceArgs,
    successAction,
    throwOnFail = true,
  } = config;

  const config_ = {
    failureAction,
    service,
    serviceName,
    successAction,
    throwOnFail,
  };
  const action = {
    payload: {
      serviceArgs,
    },
  };

  return yield call(genericApiCaller, config_, action);
}

function* reloadApiSaga(apiCaller) {
  yield takeLatest(USER_LOGIN, apiCaller);
}

function* apiServiceSaga(action, apiCaller) {
  yield takeLatest(action, apiCaller);
}

export {
  _getFailureActions,
  apiServiceSaga,
  createApiSaga,
  directServiceSaga,
  genericApiCaller,
  reloadApiSaga,
};
